import type { CoralState, HeatStressState } from './types';
import { REEF_BOTTOM } from './data/reef/bottom';
import { coralGround, grazingAnchors, grazingPressure, nextBottom, type GrazingAnchor } from './grazing';
import type { Reef, ReefIsland } from './reef';
import { DEPTH_BINS, REEF_ISLANDS, binFloorsKm2, stratumFloorKm2, stratumIndex } from './reef';
import { degreeHeatingWeeks } from './heatStress';
import { held } from './held';
import { civilFromEpochDay } from './calendar';
import { SYNODIC_MONTH_DAYS, moonAgeDays } from './astronomy';
import { CORAL_KINDS, CORAL_KIND_REEF_SHARES, type CoralKind } from './data/reef/kinds';

/**
 * The coral on an aquarium's reef (M47): its live cover in each stratum
 * (island by depth bin, `reef.ts`), as NOAA's monitoring program measures it
 * (percent of the bottom under its photographs' points), and how much of it
 * is bleached. A deterministic step that draws nothing, once a day after the
 * environment (`GDD.md` section 9: production grows toward a ceiling).
 *
 * **Growth** is logistic toward a ceiling, the colonies' own and the
 * recruits' their larvae bring, less the reef's other losses:
 *
 *   dC/dt = r C (1 - C/K) + S (1 - C/K) - m C
 *
 * and since M167 on its bottom (`grazing.ts`): r and S taken as the kind of
 * its free space lets them against the kind its survey found, and the
 * macroalgae overgrowing it past the survey's added to m.
 *
 * - Maui's growth at low cover, ln 1.12 a year: Ross 2015's model of
 *   *Montipora capitata*, "one of the most commonly observed corals" of
 *   Maui's reefs, at ten Maui sites from 1999 to 2005, its mean kernel's
 *   population growth factor 1.12 a year (the sites' 1.03 to 1.29; Ross, M.
 *   2015, PhD thesis, University of Hawaiʻi, pp. 82 and 89, as read). It
 *   holds two parts (M68): recruitment, the new colonies the sites counted
 *   (the thesis "modeled each site as a closed population where recruits
 *   were generated locally", p. 86), ρ = 0.0885 a year, MEASURED by rebuilding
 *   the thesis's model with and without them (`scripts/coralRecruitment.ts`);
 *   and the colonies' own survival, growth and splitting, r = ln 1.12 − ρ.
 * - S, the recruits settling (M68): the larvae every island's live coral
 *   spawns, as many as its reef's cells in NOAA Coral Reef Watch's larval
 *   connectivity times its cover, sent among Maui Nui's islands as that
 *   model sends them (`scripts/data/connectivity.ts`), spread over each
 *   island's cells, and taking where the coral leaves room; scaled so that
 *   Maui's recruits, its own larvae's and its neighbors', come to ρ for its
 *   cover when every island's cover is alike: Ross counted every new colony
 *   at the sites, wherever its larva came from (review M68: scaled on
 *   Maui's own larvae alone, Maui took 13% more). The islands' covers in
 *   Ross's years are not in any archive here; alike is the scale's
 *   definition.
 *   Every island's larvae settle as well as Maui's: the model says where
 *   they go, not how many do. Read for how many, its Molokaʻi larvae settle
 *   at home three times as often as Maui's, and Molokaʻi's young corals for
 *   its grown ones would be about three times Maui's; NOAA's counts find
 *   1.12 times (0.68 to 1.87), and this reading's 1.17 comes nearest them of
 *   the four readings checked, and inside every island's interval
 *   (`scripts/settlingCheck.ts`). *Montipora capitata* spawns from May
 *   to September on the nights from the new moon to the first quarter, 20:45
 *   to 22:30 local (Kolinski, S. P. and E. F. Cox 2003, *Pacific Science*
 *   57: 17, Table 1, as read), so the year's recruits settle on those nights
 *   (`isSpawningNight`), not over the weeks after, which the daily cover
 *   would not show.
 * - K, the ceiling: the cover the program's best sites hold, the 95th
 *   percentile of every site visit in the square, 2015 to 2024 (MEASURED,
 *   `scripts/coralHindcast.ts`).
 * - m, everything else the reef loses that the game does not yet make a
 *   process of (the land's sediment and nutrients, disease; the herbivores'
 *   grazing a process since M167, its surveyed state in m), as one rate: MEASURED, the rate that brings the game's coral,
 *   run from the program's 2015 survey through the record, nearest its 2016,
 *   2019 and 2024 surveys (least squares; `scripts/coralHindcast.ts`). It
 *   absorbs what the other parts miss too (see the hindcast's figures): the
 *   surveys' loss from 2016 to 2019, a fifth of the coral in years without
 *   heat, falls to it, and some of that may be 2015's deaths come late,
 *   which the game takes at the episode's peak (open, M49). The reef's
 *   whole growth at low cover, each island taking its neighbors' larvae
 *   too, is 0.1227 a year, a little above it (since M68, 0.117; 0.119 since
 *   the bottom, M167; 0.121 by kind, M176): with no heat the reef falls
 *   slowly toward a few percent (MEASURED, `npm run coralcast`: from 13.3%
 *   on 1 January 2026 to 10.6% in 2036, 7.6% in 2056 and 1.3% six centuries
 *   on).
 *
 * **Death from heat** comes with an episode's Degree Heating Weeks
 * (`heatStress.ts`), each depth bin's in its own water, cooler than the
 * surface's on the days that count by what NOAA's recorders measured
 * (`reef.ts`, M49): the share of the live coral an episode kills, by its
 * peak there, is
 *
 *   L(D) = Lmax (1 - exp(-((D - D0) / s)^k)) above D0, and 0 below it,
 *
 * D0 = 4 °C-weeks, the program's first alert level and the heat below which
 * Hughes et al. 2018 found "almost no loss of coral cover" (*Nature* 556: 492,
 * p. 5 of the accepted manuscript, as read, NOAA's repository's copy); Lmax = 0.8, its fifth level's
 * "Risk of Near Complete Mortality (> 80% of corals)"; s and k fitted by
 * least squares to Hawaii's own records, Maui Nui's of 2015 depth bin by
 * depth bin (MEASURED, `scripts/coralHindcast.ts`, which lists them). The
 * curve of Hughes et al. for the Great Barrier Reef
 * (40% lost at 4 °C-weeks, 66% at 8) does not transfer: Hawaii's corals lost
 * far less at the heat it took. The coral dies as the episode's peak rises,
 * not the weeks to months later it does on a reef (a simplification).
 *
 * **Bleaching** follows the day's Degree Heating Weeks at once, each depth
 * bin's in its own water, and fades as
 * the corals regain their color: the share bleached is the larger of a
 * logistic in the day's heat and yesterday's faded by e every
 * `BLEACH_FADE_DAYS`. Its half at 6 °C-weeks, SET BY HAND against Kāneʻohe
 * Bay in 2015, 46% of corals pale or bleached at 5.84 °C-weeks in the bay's
 * own water (Bahr et al. 2017 as quoted by Courtney et al. 2018, p. 3;
 * Matsuda et al. 2020, Table 1), with a width putting a fifth bleached at the
 * program's first alert level, 4 °C-weeks ("Risk of Reef-Wide Bleaching").
 * The fade, SET BY HAND against Matsuda et al. 2020 (*Front. Ecol. Evol.* 8:
 * 178, p. 8, as read): every bleached *Porites compressa* had its color back
 * in three months, while a third of *Montipora capitata* on one reef had and
 * none on the other; half the bleached community back in about four months.
 * Bleached coral that survives is coral: the bleaching is what the player
 * sees, and the mortality above is what it costs.
 *
 * **By kind** (M176): the coral is the user's four, lobe (*Porites lobata*),
 * finger (*P. compressa*), rice (*Montipora capitata*) and cauliflower
 * (*Pocillopora meandrina*), and the rest as one kind, each stratum's share
 * of each at the opening NOAA's adult colonies' (`scripts/data/coralKinds.ts`).
 * Every kind grows, recruits and loses as above, under one ceiling for their
 * sum (open: no growth by kind in Maui's records was found, Ross 2015's the
 * rice coral's alone), and differs in two things:
 *
 * - Its own spawning nights (Kolinski and Cox 2003, Table 1, as read): rice
 *   from May to September, new moon to first quarter, 20:45 to 22:30; finger
 *   from June to September, full moon to third quarter, 23:00 to 01:30; lobe
 *   from June to August, full moon to third quarter, 01:20 to 03:14;
 *   cauliflower in April and May, full moon to third quarter, 07:20 to 08:15
 *   in the morning; the rest on the rice coral's nights (open: the table's
 *   other *Montipora*, the reef's commonest other corals, spawn in summer on
 *   the new or the full moon). Each kind's year of recruits, ρ for its cover,
 *   shared over its own nights.
 * - How the heat kills it: a kind's survival of an episode is the reef's
 *   mean coral's, `1 - L(D)`, to a power, its susceptibility. The powers are
 *   in the ratios of the kinds' measured survival's logarithms: of the genets
 *   McCarthy et al. tracked at six Maui sites through 2015's and 2019's heat,
 *   "15.7% of Pocillopora genets survived from 2014 to 2021, compared to
 *   54.4% of M. capitata genets ... and 63.2% of P. lobata genets" (*PLoS
 *   ONE* 19: e0303779, 2024, pp. 8 and 9, CC BY, as read); the finger coral's
 *   against the lobe's as 2015's mortality in West Hawaiʻi, "Porites lobata
 *   mortality was 55%, while for P. compressa it was 33%" (Hawaiʻi DAR's
 *   *Coral Bleaching Recovery Plan*, citing Kramer et al. 2016, as read);
 *   the rest the reef's mean. Scaled so their mean over the reef's kinds
 *   (`CORAL_KIND_REEF_SHARES`) is 1, the curve L staying the reef's mean
 *   coral's, and its fit made on the community's loss (`communityLoss`).
 *   Survival over seven years holds the years' other deaths too (open);
 *   and it is the genets' survival, taken for the cover's: McCarthy et al.
 *   found a larger colony likelier to survive for *Montipora* and
 *   *Porites* and less likely for *Pocillopora* (their Fig 4), so the
 *   cover's survival would part from the genets' in opposite ways by kind
 *   (open).
 *   Bleaching stays the depth bin's, whatever the kind (open).
 *
 * **No ecological memory.** A reef that bleached once is not taken to bleach
 * less the next time: Winston et al. 2022 found at Hawaii, in 2019 after
 * 2015, "previous exposure to bleaching did not result in significant
 * overall acclimation or adaptation ... at the reef community level" (*PLoS
 * ONE* 17: e0269068, p. 15, as read).
 */

export interface CoralRates {
  /** r, the colonies' own growth, a year. */
  growthPerYear: number;
  /** ρ, recruitment's part of the growth: the recruits Maui takes, its own larvae's and its neighbors', when every island's cover is alike, a year. */
  recruitmentPerYear: number;
  /** K, percent. */
  ceilingPercent: number;
  /** m, a year. */
  otherLossPerYear: number;
  /** The heat mortality's D0, s and k, °C-weeks, and Lmax. */
  mortality: { fromDhw: number; scaleDhw: number; shape: number; most: number };
}

/** Ross 2015's mean kernel's growth factor a year, and recruitment's part of its rate (above; MEASURED, `scripts/coralRecruitment.ts`). */
const ROSS_LAMBDA = 1.12;
const RECRUITMENT_PER_YEAR = 0.0885;

/** The game's rates (above). */
export const CORAL_RATES: Readonly<CoralRates> = Object.freeze({
  growthPerYear: Math.log(ROSS_LAMBDA) - RECRUITMENT_PER_YEAR,
  recruitmentPerYear: RECRUITMENT_PER_YEAR,
  ceilingPercent: 61.2,
  otherLossPerYear: 0.121,
  mortality: Object.freeze({ fromDhw: 4, scaleDhw: 5.55, shape: 2.1, most: 0.8 }),
});

/** Bleaching's half and width, °C-weeks, and its fade, days: SET BY HAND, against what is above. */
const BLEACH_HALF_DHW = 6;
const BLEACH_WIDTH_DHW = 1.5;
export const BLEACH_FADE_DAYS = 173;
/** Days in a year: rates a year are taken over a Julian year (a definition). */
const YEAR_DAYS = 365.25;
/** A whole in percent (a definition). */
const PERCENT = 100;

/**
 * Where a reef's larvae settle (M68, `scripts/data/connectivity.ts`): each
 * island's reef cells in NOAA's model, and from each source island (row,
 * `REEF_ISLANDS` order) to each destination (column), its settlers a larva
 * relative to Maui's on Maui.
 */
export interface LarvalSettling {
  reefCells: readonly number[];
  relative: ReadonlyArray<readonly number[]>;
}

/** A spawning night's reef and where its larvae settle; on a night (`spawningOn`), the kinds that spawn (M176; none given, the rice coral's night, the coral one kind). */
export interface Spawning {
  reef: Reef;
  settling: LarvalSettling;
  kinds?: readonly CoralKind[];
}

/** A kind's spawning (above, M176): its months, the moon's ages it spawns between, as shares of the lunation, and its hours' middle, local time, hours after the night's date's midnight (past 24 the next morning). */
interface KindSpawning {
  months: { from: number; to: number };
  moon: { from: number; to: number };
  hour: number;
}
const NEW_TO_FIRST_QUARTER = { from: 0, to: 1 / 4 } as const;
const FULL_TO_THIRD_QUARTER = { from: 1 / 2, to: 3 / 4 } as const;
/** Each kind's months, moon window and hours, local, as Kolinski and Cox 2003's Table 1 gives them (the module's doc, M176). */
const KIND_SPAWNING: Readonly<Record<CoralKind, KindSpawning>> = {
  lobe: { months: { from: 6, to: 8 }, moon: FULL_TO_THIRD_QUARTER, hour: (25 + 20 / 60 + 27 + 14 / 60) / 2 },
  finger: { months: { from: 6, to: 9 }, moon: FULL_TO_THIRD_QUARTER, hour: (23 + 25.5) / 2 },
  rice: { months: { from: 5, to: 9 }, moon: NEW_TO_FIRST_QUARTER, hour: (20.75 + 22.5) / 2 },
  cauliflower: { months: { from: 4, to: 5 }, moon: FULL_TO_THIRD_QUARTER, hour: (7 + 20 / 60 + 8.25) / 2 },
  other: { months: { from: 5, to: 9 }, moon: NEW_TO_FIRST_QUARTER, hour: (20.75 + 22.5) / 2 },
};
/** Hawaii's clock, hours behind UTC (Hawaii Standard Time, kept all year: a definition). */
const HAWAII_HOURS_BEHIND_UTC = 10;
/** The days of each month of a common year (a definition). */
const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

/** Whether this date's night is one a kind spawns on (above): the moon's age at its spawning hours' middle. */
function spawnsOn(kind: CoralKind, epochDay: number): boolean {
  const k = KIND_SPAWNING[kind];
  const { month } = civilFromEpochDay(epochDay);
  if (month < k.months.from || month > k.months.to) return false;
  const age = moonAgeDays(epochDay + (k.hour + HAWAII_HOURS_BEHIND_UTC) / 24) / SYNODIC_MONTH_DAYS;
  return age >= k.moon.from && age < k.moon.to;
}

/**
 * A kind's spawning nights a year, on the long run's mean: its months' days of a common year, each a spawning night for
 * its share of the lunation (a definition; MEASURED for the rice coral, 1950 to 2149: 7,650 nights, 38.25 a year).
 */
export function spawningNightsAYear(kind: CoralKind): number {
  const k = KIND_SPAWNING[kind];
  return MONTH_DAYS.slice(k.months.from - 1, k.months.to).reduce((a, b) => a + b, 0) * (k.moon.to - k.moon.from);
}

/** *Montipora capitata*'s spawning nights a year (above): May to September's 153 days, a quarter of the lunation. */
export const SPAWNING_NIGHTS_A_YEAR = spawningNightsAYear('rice');

/** Whether this date's night is one *Montipora capitata* spawns on (above): the moon's age at the spawning hours' middle. */
export function isSpawningNight(epochDay: number): boolean {
  return spawnsOn('rice', epochDay);
}

/** The kinds that spawn on this date's night (M176, `CORAL_KINDS` order), none on most. */
export function spawningKinds(epochDay: number): CoralKind[] {
  return CORAL_KINDS.filter((k) => spawnsOn(k, epochDay));
}

/** A spawning night (`spawning` with the kinds that spawn on it) or null on a night none does. */
export function spawningOn(epochDay: number, spawning: Spawning): Spawning | null {
  const kinds = spawningKinds(epochDay);
  return kinds.length > 0 ? { ...spawning, kinds } : null;
}

/**
 * Each kind's susceptibility to the heat (above, M176): the power its survival of an episode is the reef's mean coral's
 * to, in `CORAL_KINDS` order. The lobe's, the rice's and the cauliflower's in the ratios of their survival's logarithms
 * at McCarthy et al.'s six Maui sites, 2014 to 2021; the finger's the lobe's in the ratio of their 2015 survival's in
 * West Hawaiʻi; the rest's the four's mean over the reef; all over that mean, so the reef's is 1.
 */
const SURVIVED_2014_2021 = { lobe: 0.632, rice: 0.544, cauliflower: 0.157 } as const;
const DIED_2015_WEST_HAWAII = { lobe: 0.55, finger: 0.33 } as const;
export const HEAT_SUSCEPTIBILITY: readonly number[] = (() => {
  const lobe = -Math.log(SURVIVED_2014_2021.lobe);
  const four: Record<Exclude<CoralKind, 'other'>, number> = {
    lobe,
    finger: (lobe * Math.log(1 - DIED_2015_WEST_HAWAII.finger)) / Math.log(1 - DIED_2015_WEST_HAWAII.lobe),
    rice: -Math.log(SURVIVED_2014_2021.rice),
    cauliflower: -Math.log(SURVIVED_2014_2021.cauliflower),
  };
  const weights = CORAL_KINDS.map((k, j) => (k === 'other' ? 0 : (CORAL_KIND_REEF_SHARES[j] as number)));
  const mean = CORAL_KINDS.reduce((a, k, j) => a + (k === 'other' ? 0 : four[k] * (weights[j] as number)), 0) / weights.reduce((a, b) => a + b, 0);
  return Object.freeze(CORAL_KINDS.map((k) => (k === 'other' ? 1 : four[k] / mean)));
})();

/** The share of a community's live coral an episode kills where the reef's mean coral loses `lost` (M176): each kind's share of it surviving to its power. */
export function communityLoss(lost: number, shares: readonly number[] = CORAL_KIND_REEF_SHARES): number {
  return shares.reduce((a, w, j) => a + w * (1 - (1 - lost) ** (HEAT_SUSCEPTIBILITY[j] as number)), 0);
}

/**
 * The cover a spawning night's recruits bring each island, percent of its
 * floor, before its free space takes them (above): each island's larvae, as
 * many as its reef cells in NOAA's model times its live coral cover (its
 * strata's, weighted by their floor), settling on each island as the
 * settling's matrix sends them, spread over the island's reef cells, and
 * scaled so that, every island's cover alike, Maui's come to its own cover's
 * worth; a year's recruitment shared over the year's spawning nights (a kind's own, M176).
 */
export function settledPercent(cover: readonly number[], spawning: Spawning, rates: CoralRates = CORAL_RATES, nightsAYear: number = SPAWNING_NIGHTS_A_YEAR): number[] {
  const floor = stratumFloorKm2(spawning.reef);
  const { reefCells, relative } = spawning.settling;
  const islandSum = (j: number, of: (s: number) => number): number => DEPTH_BINS.reduce((a, _, k) => a + of(stratumIndex(j, k)), 0);
  const spawned = REEF_ISLANDS.map((_, j) => {
    const area = islandSum(j, (s) => floor[s] as number);
    return area > 0 ? ((reefCells[j] as number) * islandSum(j, (s) => (floor[s] as number) * (cover[s] as number))) / area : 0;
  });
  const maui = REEF_ISLANDS.indexOf('maui');
  const mauiAlike = reefCells.reduce((a, cells, j) => a + ((relative[j] as readonly number[])[maui] as number) * cells, 0) / (reefCells[maui] as number);
  const night = rates.recruitmentPerYear / nightsAYear / mauiAlike;
  return REEF_ISLANDS.map((_, i) => {
    const arriving = spawned.reduce((a, x, j) => a + ((relative[j] as readonly number[])[i] as number) * x, 0);
    return (night * arriving) / (reefCells[i] as number);
  });
}

/** The share of the live coral an episode of this peak kills (above). */
export function heatMortality(peakDhw: number, m: CoralRates['mortality'] = CORAL_RATES.mortality): number {
  if (!(peakDhw > m.fromDhw)) return 0;
  return m.most * (1 - Math.exp(-(((peakDhw - m.fromDhw) / m.scaleDhw) ** m.shape)));
}

/** The share of the live coral bleached under this day's heat, before any fading (above): none without heat. */
function bleachingShare(dhw: number): number {
  return dhw > 0 ? 1 / (1 + Math.exp(-(dhw - BLEACH_HALF_DHW) / BLEACH_WIDTH_DHW)) : 0;
}

/**
 * The coral after one more day under this heat stress, each depth bin in its
 * own water, `fallsC` cooler than the surface's on the days that count
 * (`binHeatFallC`, M49): first the deaths a bin's rising heat brings, then a
 * day's growth and other losses and, on a spawning night (`spawning`, M68),
 * the recruits the survivors' larvae bring, then the bleaching. A bin's
 * episode is the reef's: what it has answered is cleared when the surface's
 * episode ends.
 */
/** Each stratum's grazing anchor at the coral's growth, recruitment and ceiling (`grazingAnchors`), made once for each (M167's reviewer: keyed by the rates' object, the fits' every trial made its own). */
const ANCHORS = new Map<string, GrazingAnchor[]>();
function anchorsAt(rates: CoralRates): GrazingAnchor[] {
  const key = `${rates.growthPerYear} ${rates.recruitmentPerYear} ${rates.ceilingPercent}`;
  let a = ANCHORS.get(key);
  if (!a) {
    a = grazingAnchors({ growthPerYear: rates.growthPerYear, recruitmentPerYear: rates.recruitmentPerYear, ceiling: rates.ceilingPercent / PERCENT });
    ANCHORS.set(key, a);
  }
  return a;
}

/**
 * The coral after one more day (below); with its bottom (M167), the turf and macroalgae beside it too, each island's
 * grazing at its herbivores over their tables' year's (`herbivores`, each island's ratio; 1, the tables', where none
 * is given).
 */
export function nextCoral(c: CoralState, heat: HeatStressState, fallsC: readonly number[], spawning: Spawning | null = null, rates: CoralRates = CORAL_RATES, herbivores?: readonly number[]): CoralState {
  const surviving: number[] = [];
  const answeredDhw: number[] = [];
  const bleachedPercent: number[] = [];
  fallsC.forEach((fall, k) => {
    const dhw = degreeHeatingWeeks(heat, fall);
    const before = c.answeredDhw[k] as number;
    surviving.push(dhw > before ? (1 - heatMortality(dhw, rates.mortality)) / (1 - heatMortality(before, rates.mortality)) : 1);
    answeredDhw.push(heat.peakDhw === 0 ? 0 : Math.max(before, dhw));
    bleachedPercent.push(held(Math.max(PERCENT * bleachingShare(dhw), (c.bleachedPercent[k] as number) * Math.exp(-1 / BLEACH_FADE_DAYS))));
  });
  // Each stratum's coral by kind (M176), each surviving the heat to its power; a coral of no kinds one kind of power 1.
  const kinds = c.kindPercent && c.kindPercent.length === c.coverPercent.length ? c.kindPercent : null;
  const aliveKinds = kinds
    ? kinds.map((row, i) => {
      const s = surviving[i % DEPTH_BINS.length] as number;
      return row.map((x, j) => (s === 1 ? x : x * s ** (HEAT_SUSCEPTIBILITY[j] as number)));
    })
    : c.coverPercent.map((x, i) => [x * (surviving[i % DEPTH_BINS.length] as number)]);
  // Each spawning kind's recruits, by island, over its own nights; the coral of no kinds on the rice coral's nights.
  const settledKinds: Array<number[] | null> = (kinds ? CORAL_KINDS : ['rice' as const]).map((kind, j) => {
    if (!spawning || !(spawning.kinds ?? ['rice']).includes(kind)) return null;
    return settledPercent(aliveKinds.map((row) => row[j] as number), spawning, rates, kinds ? spawningNightsAYear(kind) : SPAWNING_NIGHTS_A_YEAR);
  });
  const bottom = c.bottom && c.bottom.turfPercent.length === REEF_BOTTOM.length ? c.bottom : null;
  const anchors = bottom ? anchorsAt(rates) : null;
  const coverPercent: number[] = [];
  const kindPercent: number[][] = [];
  const turfPercent: number[] = [];
  const macroalgaePercent: number[] = [];
  aliveKinds.forEach((row, i) => {
    const island = Math.floor(i / DEPTH_BINS.length);
    const y = row.reduce((a, b) => a + b, 0);
    const free = 1 - y / rates.ceilingPercent;
    // The coral grows and settles as the kind of its space lets it, and the turf and macroalgae answer (`grazing.ts`, M167).
    const anchor = anchors ? (anchors[i] as GrazingAnchor) : null;
    const here = bottom ? { sediment: (REEF_BOTTOM[i]?.sedimentPercent ?? 0) / PERCENT, coral: y / PERCENT, turf: (bottom.turfPercent[i] as number) / PERCENT, macroalgae: (bottom.macroalgaePercent[i] as number) / PERCENT } : null;
    const ground = anchor && here ? coralGround(i, here, anchor) : { growth: 1, recruits: 1, extraLossPerYear: 0 };
    let grown = 0;
    let recruits = 0;
    const next = row.map((x, j) => {
      const g = (x * rates.growthPerYear * free * ground.growth) / YEAR_DAYS;
      const settled = settledKinds[j];
      const r = settled ? (settled[island] as number) * free * ground.recruits : 0;
      grown += g;
      recruits += r;
      // Without a bottom, the step as it was before M167, to its last bit.
      if (!anchor) return held(x + (x * (rates.growthPerYear * free - rates.otherLossPerYear)) / YEAR_DAYS + r);
      return held(x + g + r - (x * (rates.otherLossPerYear + ground.extraLossPerYear)) / YEAR_DAYS);
    });
    const cover = kinds ? held(next.reduce((a, b) => a + b, 0)) : (next[0] as number);
    coverPercent.push(cover);
    if (kinds) kindPercent.push(next);
    if (anchor && here) {
      const theta = grazingPressure(anchor, herbivores?.[island] ?? 1);
      const b = nextBottom({ ...here, coral: cover / PERCENT }, anchor, theta, grown / PERCENT, recruits / PERCENT);
      turfPercent.push(held(PERCENT * b.turf));
      macroalgaePercent.push(held(PERCENT * b.macroalgae));
    }
  });
  if (!bottom) return { ...c, coverPercent, ...(kinds ? { kindPercent } : {}), bleachedPercent, answeredDhw };
  return { coverPercent, ...(kinds ? { kindPercent } : {}), bleachedPercent, answeredDhw, bottom: { turfPercent, macroalgaePercent } };
}

/**
 * The means of the figures each stratum gives, over the strata a predicate keeps, each weighted by its floor, summed into
 * `out` (a zero for each); null over no floor.
 */
function floorMeans(reef: Reef, keep: (stratum: number) => boolean, out: number[], figures: (stratum: number) => readonly number[]): number[] | null {
  const floor = stratumFloorKm2(reef);
  let area = 0;
  floor.forEach((a, s) => {
    if (!keep(s)) return;
    figures(s).forEach((x, j) => { out[j] = (out[j] as number) + a * x; });
    area += a;
  });
  return area > 0 ? out.map((x) => x / area) : null;
}

/** The reef floor's mean live coral cover over the strata a predicate keeps, percent, weighted by their floor. */
function weighted(reef: Reef, c: CoralState, keep: (stratum: number) => boolean): number {
  return floorMeans(reef, keep, [0], (s) => [c.coverPercent[s] as number])?.[0] ?? 0;
}

/** The whole reef's live coral cover, percent, each stratum weighted by its floor. */
export function reefCover(reef: Reef, c: CoralState): number {
  return weighted(reef, c, () => true);
}

/** An island's live coral cover, percent. */
export function islandCover(reef: Reef, c: CoralState, island: ReefIsland): number {
  const i = REEF_ISLANDS.indexOf(island);
  return weighted(reef, c, (s) => Math.floor(s / DEPTH_BINS.length) === i);
}

/**
 * Each kind's live coral cover, percent of the floor (M176, `CORAL_KINDS` order), over the reef or an island, each
 * stratum weighted by its floor; null for a coral of no kinds.
 */
export function kindCovers(reef: Reef, c: CoralState, island?: ReefIsland): number[] | null {
  const kinds = c.kindPercent;
  if (!kinds || kinds.length !== c.coverPercent.length) return null;
  const i = island === undefined ? -1 : REEF_ISLANDS.indexOf(island);
  return floorMeans(reef, (s) => i < 0 || Math.floor(s / DEPTH_BINS.length) === i, CORAL_KINDS.map(() => 0), (s) => kinds[s] as number[]);
}

/**
 * An island's bottom as its floor weighs its strata (M167), percent: its live coral, turf, macroalgae and the grazed
 * EAC and coralline algae beside them (the rest of its hard bottom), or none where the coral holds no bottom.
 */
export function islandBottom(reef: Reef, c: CoralState, island: ReefIsland): { coral: number; turf: number; macroalgae: number; coralline: number } | null {
  if (!c.bottom) return null;
  const i = REEF_ISLANDS.indexOf(island);
  const m = floorMeans(reef, (s) => Math.floor(s / DEPTH_BINS.length) === i, [0, 0, 0, 0], (s) => {
    const coral = c.coverPercent[s] as number;
    const turf = c.bottom?.turfPercent[s] as number;
    const macroalgae = c.bottom?.macroalgaePercent[s] as number;
    return [coral, turf, macroalgae, Math.max(0, PERCENT - (REEF_BOTTOM[s]?.sedimentPercent ?? 0) - coral - turf - macroalgae)];
  });
  if (!m) return null;
  const [coral, turf, macroalgae, coralline] = m as [number, number, number, number];
  return { coral, turf, macroalgae, coralline };
}

/** The share of the reef's live coral bleached, percent: each stratum's live coral (its cover by its floor) bleached as its depth bin's is. */
export function reefBleached(reef: Reef, c: CoralState): number {
  const floor = stratumFloorKm2(reef);
  let live = 0;
  let bleached = 0;
  // A loop, not `forEach`: over the frozen floor `forEach` took 1.0 to 2.0 µs a call against the loop's 0.30 to 0.47, and the tick reads this twice a day (review M69, MEASURED: both over a ten-year run's 3,650 days of cover, ten passes a round, three rounds in each of two runs).
  for (let i = 0; i < floor.length; i++) {
    const x = (c.coverPercent[i] as number) * (floor[i] as number);
    live += x;
    bleached += x * (c.bleachedPercent[i % DEPTH_BINS.length] as number);
  }
  return live > 0 ? bleached / live : 0;
}

/** Each of the reef's columns' floor, km², its live coral, cover by floor, and the bleached part of it (in `reef.columns` order): its own island's strata. */
function columnCoral(reef: Reef, c: CoralState): Array<{ floor: number; live: number; bleached: number }> {
  const bins = binFloorsKm2(reef);
  return reef.columns.map((_, j) => {
    const island = reef.island[j] as number;
    const out = { floor: 0, live: 0, bleached: 0 };
    bins.forEach((b, k) => {
      const x = (c.coverPercent[stratumIndex(island, k)] as number) * (b[j] as number);
      out.floor += b[j] as number;
      out.live += x;
      out.bleached += x * (c.bleachedPercent[k] as number);
    });
    return out;
  });
}

/** Each of the reef's columns' live coral cover, percent (in `reef.columns` order): its own island's strata, weighted by its floor in each. */
export function columnCovers(reef: Reef, c: CoralState): number[] {
  return columnCoral(reef, c).map((x) => (x.floor > 0 ? x.live / x.floor : 0));
}

/** Each of the reef's columns' share of its live coral bleached, percent (in `reef.columns` order). */
export function columnBleached(reef: Reef, c: CoralState): number[] {
  return columnCoral(reef, c).map((x) => (x.live > 0 ? x.bleached / x.live : 0));
}

/**
 * The share of the live coral an episode has killed, percent: in each depth
 * bin, by the heat it answered, and over the reef, of the coral before it,
 * each stratum's taken back from its cover now (a growth's days aside); by
 * kind where the coral has them (M176), each kind's survival to its power.
 */
export function episodeKilled(reef: Reef, c: CoralState, rates: CoralRates = CORAL_RATES): { byBin: number[]; all: number } {
  const mean = c.answeredDhw.map((d) => heatMortality(d, rates.mortality));
  const kinds = c.kindPercent && c.kindPercent.length === c.coverPercent.length ? c.kindPercent : null;
  const floor = stratumFloorKm2(reef);
  const before = DEPTH_BINS.map(() => 0);
  const killed = DEPTH_BINS.map(() => 0);
  floor.forEach((a, i) => {
    const k = i % DEPTH_BINS.length;
    const lost = mean[k] as number;
    (kinds ? (kinds[i] as number[]) : [c.coverPercent[i] as number]).forEach((now, j) => {
      const share = kinds ? 1 - (1 - lost) ** (HEAT_SUSCEPTIBILITY[j] as number) : lost;
      const x = (now * a) / (1 - share);
      before[k] = (before[k] as number) + x;
      killed[k] = (killed[k] as number) + x * share;
    });
  });
  const all = before.reduce((p, x) => p + x, 0);
  const byBin = kinds ? before.map((x, k) => (x > 0 ? (killed[k] as number) / x : (mean[k] as number))) : mean;
  return { byBin: byBin.map((x) => PERCENT * x), all: all > 0 ? (PERCENT * killed.reduce((p, x) => p + x, 0)) / all : 0 };
}
